import { Box, CircularProgress, Alert } from '@mui/material';
import { useCaseFieldData } from '../../../hooks/useCaseField';
import type { FieldMode } from '../../../hooks/useCaseField';
import { useGetCaseDocumentsQuery } from '../../../../../services/MedNecService';
import DocumentCountChip from './DocumentCountChip';
import DocumentUploadZone from './DocumentUploadZone';
import DocumentList from './DocumentList';

const UPLOAD_ALLOWED_STATUSES = ['PEND', 'UREV', 'AWPR'];

interface DocumentsSectionProps {
  mode: FieldMode;
}

const DocumentsSection: React.FC<DocumentsSectionProps> = ({ mode }) => {
  const caseData = useCaseFieldData(mode);
  const caseId = caseData.id ?? '';
  const { data: docsData, isLoading, error } = useGetCaseDocumentsQuery(caseId, { skip: !caseId });

  const allowActions = UPLOAD_ALLOWED_STATUSES.includes(caseData.status ?? '');
  const documents = docsData?.documents ?? [];

  if (!caseId) return null;

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ m: 2 }}>Failed to load documents.</Alert>;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, p: 1.5 }}>
      {/* Header chips */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <DocumentCountChip mode={mode} />
      </Box>

      {/* Upload zone — only when actions are allowed */}
      {allowActions && (
        <DocumentUploadZone caseId={caseId} />
      )}

      {/* Document list with view, retag, remove */}
      <DocumentList
        caseId={caseId}
        documents={documents}
        allowActions={allowActions}
      />
    </Box>
  );
};

export default DocumentsSection;
